import { useMemo } from 'react'
import { Activity, Gauge, HeartPulse, ShieldCheck, ShoppingBag, TrendingUp } from 'lucide-react'
import {
  buildLabBiomarkerResults,
  evaluateBiomarkerStatus,
  findMatchingBiomarker,
} from '../lib/labBiomarkers'
import { C } from '../lib/tokens'
import { Card, Pill, Btn } from './UI'

const bodyKpiDefinitions = [
  {
    id: 'metabolic-efficiency',
    title: 'Metabolic efficiency',
    icon: Gauge,
    readout: 'Energy stability, glucose handling, and fuel balance.',
    lever: 'Nutrition timing, carbohydrate load, sleep, strength work.',
    markers: [
      { label: 'A1c', aliases: ['hemoglobin a1c', 'hba1c', 'a1c'] },
      { label: 'Glucose', aliases: ['glucose', 'fasting glucose'] },
      { label: 'Triglycerides', aliases: ['triglycerides'] },
      { label: 'HDL', aliases: ['hdl', 'hdl cholesterol'] },
      { label: 'Uric acid', aliases: ['uric acid'] },
    ],
  },
  {
    id: 'cardio-risk',
    title: 'Cardio risk',
    icon: HeartPulse,
    readout: 'How much vascular risk is showing up in lipids and inflammation.',
    lever: 'Body comp, fiber, training volume, medication review.',
    markers: [
      { label: 'Lp(a)', aliases: ['lipoprotein a', 'lipoprotein(a)', 'lp(a)', 'lpa'] },
      { label: 'Lp-PLA2', aliases: ['lp pla2', 'lp-pla2', 'plac test', 'plac test lp pla2'] },
      { label: 'Homocysteine', aliases: ['homocysteine'] },
      { label: 'LDL', aliases: ['ldl', 'ldl cholesterol'] },
      { label: 'Triglycerides', aliases: ['triglycerides'] },
      { label: 'hs-CRP', aliases: ['hs-crp', 'crp', 'c reactive protein', 'c-reactive protein'] },
    ],
  },
  {
    id: 'hormone-readiness',
    title: 'Hormone readiness',
    icon: Activity,
    readout: 'Signal strength for testosterone, binding, estrogen balance, and safety context.',
    lever: 'Provider protocol, sleep, body fat, alcohol, training recovery.',
    markers: [
      { label: 'Total T', aliases: ['testosterone total', 'testosterone, total', 'total testosterone'] },
      { label: 'Free T', aliases: ['testosterone free', 'testosterone, free', 'free testosterone', 'calc free testosterone', 'calculated free testosterone'] },
      { label: 'SHBG', aliases: ['sex hormone binding globulin', 'sex horm bind globulin', 'sex hormone bind globulin', 'shbg'] },
      { label: 'Estradiol', aliases: ['estradiol', 'e2'] },
      { label: 'LH', aliases: ['lh', 'luteinizing hormone'] },
    ],
  },
  {
    id: 'thyroid-throughput',
    title: 'Thyroid throughput',
    icon: TrendingUp,
    readout: 'Metabolic pace and thyroid conversion signals.',
    lever: 'Calories, iodine/selenium sufficiency, stress load, medication review.',
    markers: [
      { label: 'TSH', aliases: ['tsh', 'thyroid stimulating hormone'] },
      { label: 'Free T4', aliases: ['free t4', 't4 free', 'thyroxine free'] },
      { label: 'Free T3', aliases: ['free t3', 't3 free', 'triiodothyronine free'] },
      { label: 'TPO Ab', aliases: ['thyroid peroxidase', 'tpo antibody', 'tpo ab'] },
    ],
  },
  {
    id: 'inflammation-load',
    title: 'Inflammation load',
    icon: ShieldCheck,
    readout: 'Background stress from immune activation, iron handling, and tissue load.',
    lever: 'Recovery, infection/injury review, nutrition quality, gut work.',
    markers: [
      { label: 'hs-CRP', aliases: ['hs-crp', 'crp', 'c reactive protein', 'c-reactive protein'] },
      { label: 'Ferritin', aliases: ['ferritin'] },
      { label: 'WBC', aliases: ['wbc', 'white blood cell', 'white blood cells'] },
      { label: 'Neutrophils', aliases: ['neutrophils'] },
    ],
  },
  {
    id: 'recovery-capacity',
    title: 'Recovery capacity',
    icon: Activity,
    readout: 'Oxygen delivery and training recovery guardrails.',
    lever: 'Training load, hydration, iron status, sleep quality.',
    markers: [
      { label: 'Hemoglobin', aliases: ['hemoglobin'] },
      { label: 'Hematocrit', aliases: ['hematocrit'] },
      { label: 'RBC', aliases: ['rbc', 'red blood cell', 'red blood cells'] },
      { label: 'Ferritin', aliases: ['ferritin'] },
      { label: 'B12', aliases: ['vitamin b12', 'b12'] },
    ],
  },
  {
    id: 'organ-resilience',
    title: 'Organ resilience',
    icon: ShieldCheck,
    readout: 'Liver, kidney, and filtration capacity before protocols intensify.',
    lever: 'Hydration, alcohol, medication review, protein load, body comp.',
    markers: [
      { label: 'ALT', aliases: ['alt', 'alanine aminotransferase'] },
      { label: 'AST', aliases: ['ast', 'aspartate aminotransferase'] },
      { label: 'Creatinine', aliases: ['creatinine'] },
      { label: 'eGFR', aliases: ['egfr', 'estimated gfr'] },
      { label: 'BUN', aliases: ['bun', 'blood urea nitrogen'] },
    ],
  },
  {
    id: 'nutrient-reserves',
    title: 'Nutrient reserves',
    icon: Gauge,
    readout: 'Raw materials for energy, mood, hormones, and recovery.',
    lever: 'Diet quality, supplementation, sun exposure, gut absorption.',
    markers: [
      { label: 'Vitamin D', aliases: ['vitamin d', '25-hydroxyvitamin d', '25 hydroxy vitamin d'] },
      { label: 'B12', aliases: ['vitamin b12', 'b12'] },
      { label: 'Ferritin', aliases: ['ferritin'] },
      { label: 'Magnesium', aliases: ['magnesium'] },
      { label: 'Folate', aliases: ['folate'] },
      { label: 'Zinc', aliases: ['zinc'] },
    ],
  },
]

const STATUS_SCORE = {
  green: 100,
  yellow: 70,
  red: 30,
}

const STAGE_META = {
  optimized: {
    label: 'Optimized',
    color: 'green',
    border: 'rgba(0,191,109,0.24)',
    bg: 'linear-gradient(180deg, #F4FFF8 0%, #FFFFFF 100%)',
    iconBg: 'linear-gradient(180deg, #05C875 0%, #00A85F 100%)',
    iconColor: '#fff',
    bar: 'linear-gradient(90deg, #00BF6D, #30D158)',
  },
  wellness: {
    label: 'Wellness',
    color: 'blue',
    border: 'rgba(0,122,255,0.18)',
    bg: 'linear-gradient(180deg, #FFFFFF 0%, #F6FAFF 100%)',
    iconBg: 'linear-gradient(180deg, #F8FAFC 0%, #EEF6FF 100%)',
    iconColor: C.blueDark,
    bar: 'linear-gradient(90deg, #007AFF, #5E5CE6)',
  },
  sickness: {
    label: 'Sickness',
    color: 'red',
    border: 'rgba(255,59,48,0.22)',
    bg: 'linear-gradient(180deg, #FFF8F6 0%, #FFFFFF 100%)',
    iconBg: 'linear-gradient(180deg, #FFF4EF 0%, #FFE7DE 100%)',
    iconColor: '#A6201B',
    bar: 'linear-gradient(90deg, #FF3B30, #FF9500)',
  },
  pending: {
    label: 'Needs data',
    color: 'default',
    border: C.border,
    bg: 'linear-gradient(180deg, #FFFFFF 0%, #FBFCFE 100%)',
    iconBg: 'linear-gradient(180deg, #F8FAFC 0%, #F1F4F8 100%)',
    iconColor: C.text,
    bar: 'linear-gradient(90deg, #AEB6C2, #D8DDE6)',
  },
}

function parseLocalDate(value) {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  const text = String(value).trim()
  const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dateOnly) {
    const date = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    return Number.isNaN(date.getTime()) ? null : date
  }
  const date = new Date(text)
  return Number.isNaN(date.getTime()) ? null : date
}

function dateInputValue(value) {
  const date = parseLocalDate(value)
  if (!date) return null
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

function formatDate(value) {
  const date = parseLocalDate(value)
  if (!date) return null
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function normalizeCollectionDate(value) {
  const dateOnly = dateInputValue(value)
  if (!dateOnly) return null
  const date = parseLocalDate(dateOnly)
  const today = parseLocalDate(dateInputValue(new Date()))
  const oldest = new Date(today.getFullYear() - 10, today.getMonth(), today.getDate())
  if (date > today || date < oldest) return null
  return dateOnly
}

function normalizeDateParts(month, day, year) {
  const normalizedYear = Number(year) < 100
    ? Number(year) >= 70 ? 1900 + Number(year) : 2000 + Number(year)
    : Number(year)
  const date = new Date(normalizedYear, Number(month) - 1, Number(day))
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== normalizedYear ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day)
  ) {
    return null
  }
  return normalizeCollectionDate(date)
}

function collectionDateFromText(value) {
  const text = String(value || '')
  if (!text) return null
  const iso = text.match(/\b(20\d{2})[-_/.](0?[1-9]|1[0-2])[-_/.](0?[1-9]|[12]\d|3[01])\b/)
  if (iso) return normalizeDateParts(iso[2], iso[3], iso[1])
  const monthDayYear = [...text.matchAll(/\b(0?[1-9]|1[0-2])[-_/.](0?[1-9]|[12]\d|3[01])[-_/.](\d{2}|\d{4})\b/g)]
  for (const match of monthDayYear) {
    const normalized = normalizeDateParts(match[1], match[2], match[3])
    if (normalized) return normalized
  }
  return null
}

function labDateStrings(lab = {}) {
  return [
    lab?.fileName,
    lab?.filename,
    lab?.lab,
    lab?.lab_company,
    lab?.title,
    lab?.display_name,
    lab?.displayName,
    lab?.metadata?.fileName,
    lab?.metadata?.filename,
    lab?.metadata?.originalName,
    lab?.metadata?.display_name,
    lab?.metadata?.displayName,
    lab?.document?.display_name,
    lab?.pdf_document?.display_name,
    lab?.requisition_document?.display_name,
    ...(Array.isArray(lab?.lab_results) ? lab.lab_results : []).flatMap(result => [
      result?.display_name,
      result?.document?.display_name,
    ]),
  ].filter(Boolean)
}

function labCollectionDate(lab = {}) {
  const explicit = [
    lab?.collected_at,
    lab?.collectedAt,
    lab?.collection_date,
    lab?.collectionDate,
    lab?.specimen_collected_at,
    lab?.specimenCollectedAt,
    lab?.sample_collected_at,
    lab?.sampleCollectedAt,
    lab?.draw_date,
    lab?.drawDate,
    lab?.metadata?.collected_at,
    lab?.metadata?.collectedAt,
    lab?.metadata?.collection_date,
    lab?.metadata?.collectionDate,
    lab?.metadata?.specimen_collected_at,
    lab?.metadata?.specimenCollectedAt,
    lab?.metadata?.sample_collected_at,
    lab?.metadata?.sampleCollectedAt,
    lab?.metadata?.draw_date,
    lab?.metadata?.drawDate,
    lab?.test_date,
  ].map(normalizeCollectionDate).find(Boolean)
  if (explicit) return explicit
  return labDateStrings(lab).map(collectionDateFromText).find(Boolean) || null
}

function labReferenceDate(lab = {}) {
  return labCollectionDate(lab)
}

function matchMarkerResult(results, marker) {
  return findMatchingBiomarker(results, {
    ...marker,
    aliases: [marker.label, ...(marker.aliases || [])],
  })
}

function markerCompletenessKey(marker = {}) {
  return String(marker.label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function dashboardCompleteness(results = []) {
  const uniqueMarkers = new Map()
  bodyKpiDefinitions.forEach(kpi => {
    kpi.markers.forEach(marker => {
      const key = markerCompletenessKey(marker)
      if (key && !uniqueMarkers.has(key)) uniqueMarkers.set(key, marker)
    })
  })

  const markers = [...uniqueMarkers.values()]
  const found = markers.filter(marker => matchMarkerResult(results, marker)).length
  return {
    found,
    total: markers.length,
    score: markers.length ? Math.round((found / markers.length) * 100) : 0,
  }
}

function markerScore(status) {
  return status ? STATUS_SCORE[status.level] ?? null : null
}

function markerValueLabel(result) {
  if (!result || result.value === null || result.value === undefined) return ''
  const value = String(result.value).trim()
  if (!value) return ''
  const unit = String(result.unit || '').trim()
  if (!unit || value.toLowerCase().includes(unit.toLowerCase()) || unit.length > 10) return value
  return `${value} ${unit}`
}

function markerChipPalette(status) {
  if (status?.level === 'green') {
    return { border: '#BFEFD5', bg: '#EAF8F1', fg: C.greenDark }
  }
  if (status?.level === 'yellow') {
    return { border: 'rgba(245,158,11,0.22)', bg: 'rgba(255,249,230,0.96)', fg: '#7A5A0B' }
  }
  if (status?.level === 'red') {
    return { border: 'rgba(255,59,48,0.22)', bg: 'rgba(253,234,234,0.96)', fg: '#A6201B' }
  }
  if (status) {
    return { border: C.border, bg: '#F7F9FC', fg: C.textSecondary }
  }
  return { border: C.border, bg: '#FAFAFC', fg: C.textSecondary }
}

function kpiFocus(markerEntries) {
  const sickness = markerEntries.find(entry => entry.status?.level === 'red')
  if (sickness) return `Bring ${sickness.marker.label} toward optimized range.`
  const missing = markerEntries.find(entry => !entry.result)
  if (missing) return `Add ${missing.marker.label} to complete the KPI.`
  const wellness = markerEntries.find(entry => entry.score !== null && entry.status?.level !== 'green')
  if (wellness) return `Optimize ${wellness.marker.label} next.`
  const needsRange = markerEntries.find(entry => entry.result && entry.score === null)
  if (needsRange) return `Add an optimized range for ${needsRange.marker.label}.`
  return 'Maintain every tracked marker in optimized range.'
}

function evaluateKpi(kpi, biomarkerResults) {
  const markerEntries = kpi.markers.map(marker => {
    const result = matchMarkerResult(biomarkerResults, marker)
    const status = result ? evaluateBiomarkerStatus(result) : null
    return {
      marker,
      result,
      status,
      score: markerScore(status),
    }
  })
  const foundMarkers = markerEntries.filter(entry => entry.result)
  const scoreable = markerEntries.filter(entry => entry.score !== null)
  const valueScore = scoreable.length
    ? Math.round(scoreable.reduce((sum, entry) => sum + entry.score, 0) / scoreable.length)
    : null
  const completion = kpi.markers.length ? foundMarkers.length / kpi.markers.length : 0
  const hasSickness = scoreable.some(entry => entry.status?.level === 'red')
  const isOptimized =
    completion === 1 &&
    scoreable.length === kpi.markers.length &&
    scoreable.every(entry => entry.status?.level === 'green')
  const stageKey = !scoreable.length
    ? 'pending'
    : hasSickness
      ? 'sickness'
      : isOptimized
        ? 'optimized'
        : 'wellness'

  return {
    ...kpi,
    markerEntries,
    foundMarkers,
    scoreableMarkers: scoreable,
    completion,
    valueScore,
    stageKey,
    stage: STAGE_META[stageKey],
    focus: kpiFocus(markerEntries),
  }
}

function gaugePoint(cx, cy, radius, value) {
  const clamped = Math.max(0, Math.min(100, Number(value) || 0))
  const angle = (180 - (clamped / 100) * 180) * (Math.PI / 180)
  return {
    x: cx + radius * Math.cos(angle),
    y: cy - radius * Math.sin(angle),
  }
}

function gaugeArcPath(start, end, radius = 70) {
  const startPoint = gaugePoint(90, 88, radius, start)
  const endPoint = gaugePoint(90, 88, radius, end)
  return `M ${startPoint.x.toFixed(2)} ${startPoint.y.toFixed(2)} A ${radius} ${radius} 0 0 1 ${endPoint.x.toFixed(2)} ${endPoint.y.toFixed(2)}`
}

function ScoreGauge({ score, stage }) {
  const hasScore = score !== null && score !== undefined
  const displayScore = hasScore ? Math.max(0, Math.min(100, score)) : 0
  const needle = gaugePoint(90, 88, 54, displayScore)
  const tickValues = [0, 25, 50, 75, 100]

  return (
    <div style={{
      position: 'relative',
      minHeight: 112,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'end',
    }}>
      <svg viewBox="0 0 180 112" role="img" aria-label={hasScore ? `KPI score ${displayScore}` : 'KPI score waiting'} style={{
        width: '100%',
        maxWidth: 230,
        height: 112,
        overflow: 'visible',
      }}>
        <path d={gaugeArcPath(0, 100, 70)} fill="none" stroke="#E9EDF3" strokeWidth="18" strokeLinecap="butt" />
        <path d={gaugeArcPath(0, 35, 70)} fill="none" stroke="#FF6B63" strokeWidth="18" strokeLinecap="butt" />
        <path d={gaugeArcPath(35, 65, 70)} fill="none" stroke="#FFD978" strokeWidth="18" strokeLinecap="butt" />
        <path d={gaugeArcPath(65, 85, 70)} fill="none" stroke="#A9D6F5" strokeWidth="18" strokeLinecap="butt" />
        <path d={gaugeArcPath(85, 100, 70)} fill="none" stroke="#9DDBA3" strokeWidth="18" strokeLinecap="butt" />

        {tickValues.map(value => {
          const outer = gaugePoint(90, 88, 79, value)
          const inner = gaugePoint(90, 88, 62, value)
          const label = gaugePoint(90, 88, 49, value)
          return (
            <g key={value}>
              <line x1={inner.x} y1={inner.y} x2={outer.x} y2={outer.y} stroke="rgba(17,19,24,0.28)" strokeWidth="1.5" />
              <text x={label.x} y={label.y + 3} textAnchor="middle" fontSize="8.5" fontWeight="760" fill={C.textTertiary}>{value}</text>
            </g>
          )
        })}

        {hasScore && (
          <>
            <line x1="90" y1="88" x2={needle.x} y2={needle.y} stroke={stage.iconColor === '#fff' ? C.greenDark : stage.iconColor} strokeWidth="2.6" strokeLinecap="round" />
            <circle cx="90" cy="88" r="5.5" fill={stage.iconColor === '#fff' ? C.greenDark : stage.iconColor} />
          </>
        )}

      </svg>
    </div>
  )
}

export default function BodyKpiDashboard({ labs, onOrder }) {
  const biomarkerResults = useMemo(() => buildLabBiomarkerResults(labs || [], { requireValue: true }), [labs])
  const kpis = useMemo(() => bodyKpiDefinitions.map(kpi => evaluateKpi(kpi, biomarkerResults)), [biomarkerResults])
  const completeness = useMemo(() => dashboardCompleteness(biomarkerResults), [biomarkerResults])
  const completenessScore = completeness.score
  const scoreableKpis = kpis.filter(kpi => kpi.valueScore !== null)
  const bodyScore = scoreableKpis.length
    ? Math.round(scoreableKpis.reduce((sum, kpi) => sum + kpi.valueScore, 0) / scoreableKpis.length)
    : null
  const optimizedCount = kpis.filter(kpi => kpi.stageKey === 'optimized').length
  const wellnessCount = kpis.filter(kpi => kpi.stageKey === 'wellness').length
  const sicknessCount = kpis.filter(kpi => kpi.stageKey === 'sickness').length
  const latestDate = (labs || [])
    .map(labReferenceDate)
    .filter(Boolean)
    .sort((a, b) => (Date.parse(b) || 0) - (Date.parse(a) || 0))[0]

  return (
    <Card className="body-kpi-shell" style={{
      padding: 0,
      borderRadius: 22,
      overflow: 'hidden',
      boxShadow: C.shadowMedium,
    }}>
      <div className="body-kpi-header" style={{
        padding: '20px 22px',
        borderBottom: `1px solid ${C.border}`,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        gap: 16,
        alignItems: 'center',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className="body-kpi-main-icon" style={{
              width: 38,
              height: 38,
              borderRadius: 19,
              background: 'linear-gradient(180deg, #F8FAFC 0%, #F1F4F8 100%)',
              color: C.text,
              border: `1px solid ${C.hairline}`,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Gauge size={18} strokeWidth={2.2} />
            </span>
            <div style={{ fontSize: 20, lineHeight: 1.15, fontWeight: 820, color: C.text, letterSpacing: 0 }}>
              Health KPI Dashboard
            </div>
          </div>
          <div style={{ marginTop: 7, maxWidth: 760, fontSize: 13, color: C.textSecondary, lineHeight: 1.45 }}>
            Track your body like a business: improve the numbers from sickness to wellness to optimized, then review over time.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Pill color={optimizedCount ? 'green' : 'default'}>{optimizedCount} optimized</Pill>
          <Pill color={wellnessCount ? 'blue' : 'default'}>{wellnessCount} wellness</Pill>
          <Pill color={sicknessCount ? 'red' : 'default'}>{sicknessCount} sickness</Pill>
          <Pill color={completenessScore ? 'purple' : 'default'}>{completenessScore}% complete</Pill>
        </div>
      </div>

      <div style={{
        padding: 22,
        display: 'grid',
        gap: 18,
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 10,
        }} className="body-kpi-summary-grid">
          {[
            { label: 'Body score', value: bodyScore !== null ? `${bodyScore}/100` : 'Waiting', copy: 'Sickness to Wellness to Optimized.' },
            { label: 'Optimized KPIs', value: `${optimizedCount}/${kpis.length}`, copy: 'Goal: every KPI in optimized range.' },
            { label: 'Completeness', value: `${completenessScore}%`, copy: `${completeness.found}/${completeness.total} unique KPI markers captured.` },
            { label: 'Latest collection', value: latestDate ? formatDate(latestDate) : 'Waiting', copy: latestDate ? 'Review every 90 days.' : 'Add a collection date from the report.' },
          ].map(item => (
            <div key={item.label} className="body-kpi-stat-card" style={{
              border: `1px solid ${C.border}`,
              borderRadius: 18,
              padding: 14,
              background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.82)',
            }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: C.textTertiary, textTransform: 'uppercase', letterSpacing: 0 }}>{item.label}</div>
              <div style={{ marginTop: 6, fontSize: 17, fontWeight: 820, color: C.text, lineHeight: 1.15 }}>{item.value}</div>
              <div style={{ marginTop: 5, fontSize: 12.4, color: C.textSecondary, lineHeight: 1.35 }}>{item.copy}</div>
            </div>
          ))}
        </div>

        <div className="body-kpi-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gridAutoRows: '1fr',
          alignItems: 'stretch',
          gap: 12,
        }}>
          {kpis.map(kpi => {
            const Icon = kpi.icon
            const stage = kpi.stage
            return (
              <div key={kpi.id} className={`body-kpi-card body-kpi-card-${kpi.stageKey}${kpi.stageKey === 'optimized' ? ' is-ready' : ''}`} style={{
                border: `1px solid ${stage.border}`,
                borderRadius: 18,
                padding: 15,
                background: stage.bg,
                boxShadow: C.shadowSoft,
                display: 'grid',
                gap: 12,
                alignContent: 'start',
                height: '100%',
                minHeight: 236,
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr)', gap: 10, alignItems: 'center' }}>
                  <span className="body-kpi-card-icon" style={{
                    width: 34,
                    height: 34,
                    borderRadius: 17,
                    background: stage.iconBg,
                    color: stage.iconColor,
                    border: `1px solid ${kpi.stageKey === 'optimized' ? 'rgba(255,255,255,0.28)' : C.hairline}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Icon size={17} strokeWidth={2} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 800, color: C.text, lineHeight: 1.2 }}>{kpi.title}</div>
                    <div style={{ marginTop: 4, fontSize: 12.1, color: C.textSecondary, lineHeight: 1.35 }}>{kpi.readout}</div>
                  </div>
                </div>

                <div>
                  <ScoreGauge score={kpi.valueScore} stage={stage} />
                  <div style={{
                    marginTop: 2,
                    display: 'flex',
                    justifyContent: 'center',
                  }}>
                    <Pill color={stage.color}>
                      {kpi.valueScore !== null ? `${stage.label} ${kpi.valueScore}/100` : stage.label}
                    </Pill>
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr)',
                    gap: 8,
                    marginTop: 10,
                    marginBottom: 9,
                  }}>
                    <div>
                      <div style={{ fontSize: 10.5, fontWeight: 800, color: C.textTertiary, textTransform: 'uppercase', letterSpacing: 0 }}>Completeness</div>
                      <div style={{ marginTop: 3, fontSize: 15, fontWeight: 820, color: C.text, lineHeight: 1 }}>
                        {kpi.foundMarkers.length}/{kpi.markers.length}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 7, fontSize: 12.2, color: C.textSecondary, lineHeight: 1.35 }}>
                    Focus: {kpi.focus}
                  </div>
                  <div style={{ marginTop: 5, fontSize: 12.2, color: C.textSecondary, lineHeight: 1.35 }}>
                    Moves with: {kpi.lever}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {kpi.markerEntries.map(({ marker, result, status }) => {
                    const palette = markerChipPalette(status)
                    const value = markerValueLabel(result)
                    return (
                      <span key={marker.label} style={{
                        borderRadius: 999,
                        border: `1px solid ${palette.border}`,
                        background: palette.bg,
                        color: palette.fg,
                        padding: '4px 8px',
                        fontSize: 11.2,
                        fontWeight: 720,
                        lineHeight: 1,
                        maxWidth: '100%',
                        overflowWrap: 'anywhere',
                      }}>
                        {value ? `${marker.label} ${value}` : marker.label}
                      </span>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        <div style={{
          border: `1px solid ${C.border}`,
          borderRadius: 18,
          padding: 16,
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F7FBFF 100%)',
          boxShadow: C.shadowSoft,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 800, color: C.text }}>Best next data set</div>
            <div style={{ marginTop: 4, fontSize: 12.8, color: C.textSecondary, lineHeight: 1.4, maxWidth: 720 }}>
              The comprehensive panel improves the completeness score and gives more numbers to move from sickness and wellness into optimized ranges.
            </div>
          </div>
          <Btn variant="green" onClick={onOrder}>
            <ShoppingBag size={15} />
            Order labs
          </Btn>
        </div>
      </div>
    </Card>
  )
}
